import { useState, useLayoutEffect } from "react";

/**
 * Safely acquires a DOM element by ID for use as a React Portal target.
 *
 * Two-tier resolution for zero-flicker portal mounting:
 *   1. Lazy initializer: on navigation (not first load), portal divs already
 *      exist in the DOM, so getElementById succeeds during useState init.
 *      Result: correct portal target on the very first render — no re-render.
 *   2. useLayoutEffect fallback: on initial page load, the portal divs haven't
 *      been committed yet during the render phase. useLayoutEffect resolves them
 *      synchronously after commit but BEFORE the browser paints — still no flicker.
 *
 * Usage:
 *   const portalTarget = usePortalTarget("my-portal-id");
 *   return portalTarget ? createPortal(content, portalTarget) : content;
 *
 * Portal targets are created in Router.tsx (NewUI component):
 *   - "progress-bar-portal" — above the animated Routes
 *   - "bottom-summary-portal" — below the animated Routes
 */
export function usePortalTarget(portalId: string): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(
    () => document.getElementById(portalId),
  );

  useLayoutEffect(() => {
    if (!target) {
      const el = document.getElementById(portalId);
      if (el) setTarget(el);
    }
  }, [portalId, target]);

  return target;
}
